import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EntityList } from 'app/models/entity-list.model';
import { omit } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { Admin } from '../models/admin.model';
import { AuthService } from './auth.service';
import { toHttpParams } from './http-query-params';

@Injectable({ providedIn: 'root' })
export class AdminService {
  private _http = inject(HttpClient);
  protected authService = inject(AuthService);

  update = new Subject<{ operation: string; value: Admin[] }>();

  addAdmin(admin: Admin): Observable<EmptyMutationResponse> {
    return this._http.post<void>('/api/admin', admin, { observe: 'response' }).pipe(
      map(res => {
        this.update.next({ operation: 'addAdmin', value: [] });

        return toEmptyMutationResponse(res);
      }),
    );
  }

  deleteAdmin(adminId: number, replaceAdminId: number): Observable<{ statusCode: number }> {
    return this._http.delete<void>('/api/admin/' + adminId + '/' + replaceAdminId, { observe: 'response' }).pipe(
      map(res => {
        this.update.next({ operation: 'deleteAdmin', value: [] });

        return toEmptyMutationResponse(res);
      }),
    );
  }

  getAdmin(adminId: number): Observable<{ statusCode: number; data: Admin }> {
    return this._http
      .get<{ statusCode: number; data: Admin }>('/api/admin/' + adminId, { observe: 'response' })
      .pipe(map(res => res.body!));
  }

  getAdminList(query: AdminQuery = {}): Observable<{ statusCode: number; data: EntityList<Admin> }> {
    const route: Record<keyof AdminQuery, string> = { ids: 'ids', offset: 'offset', limit: 'limit', like: 'like', exclude: 'exclude' };

    return this._http
      .get<{ statusCode: number; data: EntityList<Admin> }>('/api/admin/list', { observe: 'response', params: toHttpParams(query, route) })
      .pipe(map(res => res.body!));
  }

  setActivation(adminId: number, activation: boolean): Observable<EmptyMutationResponse> {
    return this._http.put<void>('/api/admin', { id: adminId, isActive: activation }, { observe: 'response' }).pipe(
      map(res => {
        this.update.next({ operation: 'setActivation', value: [] });

        return toEmptyMutationResponse(res);
      }),
    );
  }

  updateAdmin(admin: Partial<Admin>): Observable<EmptyMutationResponse> {
    const payload = this.authService.isSuperAdmin() ? admin : omit(admin, ['allowedPPF', 'allowedWindows', 'isActive']);

    return this._http.put<void>('/api/admin', payload, { observe: 'response' }).pipe(
      map(res => {
        this.update.next({ operation: 'updateAdmin', value: [] });

        return toEmptyMutationResponse(res);
      }),
    );
  }

  setPassword(passwords: { currentPassword: string; newPassword: string }): Observable<EmptyMutationResponse> {
    return this._http.put<void>('/api/admin/set-password', passwords, { observe: 'response' }).pipe(map(toEmptyMutationResponse));
  }

  updateCredit(payload: { window: number; ppf: number; id: number }): Observable<Admin> {
    return this._http.put<{ data: Admin }>('/api/admin/credit', payload).pipe(map(response => response.data));
  }
}

type EmptyMutationResponse = { statusCode: number };

const toEmptyMutationResponse = (response: HttpResponse<unknown>): EmptyMutationResponse => ({ statusCode: response.status });

export type AdminQuery = {
  ids?: number[];
  limit?: number;
  offset?: number;
  like?: string;
  exclude?: number[];
};
